import { SportsProductService } from '@frontend/sports/host-app/sports-product/feature/sports-product-service';
import { Observable, OperatorFunction, distinctUntilChanged, filter, map, skip, tap, withLatestFrom } from 'rxjs';

import { APP_INJECTOR } from './app-injector';

export function fromSportsLeave(productService?: SportsProductService): Observable<boolean> {
    const service = productService ?? APP_INJECTOR.get(SportsProductService);
    return service.isSportsProductActive$.pipe(
        skip(1),
        distinctUntilChanged(),
        filter((x) => !x),
    );
}

export function fromSportsEnter(productService?: SportsProductService): Observable<boolean> {
    const service = productService ?? APP_INJECTOR.get(SportsProductService);
    return service.isSportsProductActive$.pipe(
        skip(1),
        distinctUntilChanged(),
        filter((x) => x),
    );
}

/**
 * Emitting items if the current product is SPORTS
 */
export function filterSports<T>(productService?: SportsProductService): OperatorFunction<T, T> {
    const service = productService ?? APP_INJECTOR.get(SportsProductService);

    return (source$: Observable<T>) =>
        source$.pipe(
            withLatestFrom(service.isSportsProductActive$),
            filter(([_, isSports]) => isSports),
            map(([value]) => value),
        );
}

/**
 * Emitting items if the current product is SPORTS and emits the last non-SPORTS item whenever a SPORTS product is detected.
 */
export function filterSportsEmitLast<T>(productService?: SportsProductService): OperatorFunction<T, T> {
    const service = productService ?? APP_INJECTOR.get(SportsProductService);
    let lastNotSportsValue: T | null = null;

    return (source$) =>
        new Observable((subscriber) => {
            const productSubscription = service.isSportsProductActive$.pipe(filter((x) => x)).subscribe(() => {
                if (lastNotSportsValue) {
                    subscriber.next(lastNotSportsValue);
                    lastNotSportsValue = null;
                }
            });

            const sourceSubscription = source$
                .pipe(
                    withLatestFrom(service.isSportsProductActive$),
                    tap(([value, isSports]) => {
                        lastNotSportsValue = isSports ? null : value;
                    }),
                    filter(([_, isSports]) => isSports),
                    map(([value]) => value),
                )
                .subscribe({
                    next: (value) => {
                        subscriber.next(value);
                    },
                    error: (err) => {
                        subscriber.error(err);
                    },
                    complete: () => {
                        subscriber.complete();
                    },
                });

            return () => {
                productSubscription.unsubscribe();
                sourceSubscription.unsubscribe();
            };
        });
}
